import { Component, Input, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { ProgressBarMode } from '@angular/material/progress-bar';
import { EFundStatus, FundTypeEnum, OpportunityItemDto } from 'src/app/core/data-models/opportunity.data.models';
import { LanguageService } from '../../services/language.service';
import { AppTheme, TenantConfigService } from '../../services/tenant-config.service';
import { MatDialog } from '@angular/material/dialog';
import { RoiFormatPipe } from '../../pips/roi-format.pipe';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-opportunity-card',
  templateUrl: './opportunity-card.component.html',
  styleUrls: ['./opportunity-card.component.scss']
})
export class OpportunityCardComponent {
  @Input() opportunity!: OpportunityItemDto;
  public fundStatusLabel!: string;
  @Input() filterFundStatus?: EFundStatus;
  fundStatus = EFundStatus;
  fundTypes = FundTypeEnum;
  color: ThemePalette = 'primary';
  mode: ProgressBarMode = 'determinate';
  opportunityStatus!: EFundStatus;
  shortFundType!: string;
  shortStatus!: string;
  formattedRoi!: string;
  cardImageUrl: string = '';
  categoryLabel: string = '';
  showDateDetails: boolean = false;
  startDate?: string | Date;
  endDate?: string | Date;
  availableUnits: number = 0;
  defaultOpportunityImageUrl: string = '';
  tenantId: string = '';
  opportunityTheme: AppTheme = AppTheme.CLASSIC;
  opportunityThemes = AppTheme;
  fundTitleFontSize!: string;
  classicFundTitleFontSize!: string;
  @ViewChild('filterDialogRef') filterDialogRef!: TemplateRef<any>;
  private roiFormatPipe = new RoiFormatPipe();

  constructor(
    public languageService: LanguageService,
    private tenantConfigService: TenantConfigService,
    private translateService: TranslateService,
  ) {
    this.defaultOpportunityImageUrl = this.tenantConfigService.tenant.defaultOpportunityImageUrl;
    this.tenantId = this.tenantConfigService.tenant.tenantId;
    this.opportunityTheme = this.tenantConfigService.tenant.opportunityTheme;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['opportunity'] && this.opportunity) {
      this.calculateDerivedData();
    }
  }

  private calculateDerivedData(): void {
    const isEn = this.languageService.currentLanguage === 'en';
    this.opportunityStatus = this.mapStatusToEnum(this.opportunity.fundStatus as unknown as string);
    this.shortFundType = this.opportunity.fundType === FundTypeEnum.CloseFund
      ? (isEn ? 'Close' : 'مغلق')
      : (isEn ? 'Open' : 'مفتوح');
    this.fundStatusLabel = this.getLabelForStatus(this.opportunity.fundStatus as unknown as string, isEn);
    const roi = this.opportunity.expectedTotalProfit;
    const roiText = this.roiFormatPipe.transform(roi);
    this.formattedRoi = roiText && !roiText.includes('%') ? `${roiText}%` : roiText;

    this.cardImageUrl = this.opportunity.coverPhotoURL
      || this.opportunity.investmentFundLogoUrl
      || this.opportunity.companylogoUrl
      || this.defaultOpportunityImageUrl;
    this.categoryLabel = this.getCategoryLabel(this.opportunity.companyType);
    this.showDateDetails = this.opportunity.fundType === FundTypeEnum.CloseFund
      || this.opportunityStatus === EFundStatus.Upcoming;
    this.startDate = this.opportunity.offeringStartDate || this.opportunity.investmentStartDate;
    this.endDate = this.opportunity.offeringEndDate || this.opportunity.investmentEndDate;
    this.availableUnits = Math.max(0, this.opportunity.totalAvailableUnits ?? 0);

    const fundName = this.opportunity.fundNameWithMultiLine || this.opportunity.fundName || '';
    this.fundTitleFontSize = this.getScaledFontSize(1.35, fundName);
    this.classicFundTitleFontSize = this.getScaledFontSize(1.37, fundName);
  }

  private getScaledFontSize(baseRem: number, text: string): string {
    const length = text.replace(/\n/g, '').length;
    let scale = 1;
    if (length > 60) {
      scale = 0.55;
    } else if (length > 45) {
      scale = 0.65;
    } else if (length > 30) {
      scale = 0.8;
    } else if (length > 20) {
      scale = 0.9;
    }
    return `${this.snapToTypeScale(baseRem * scale * 16)}px`;
  }

  /** Snap a pixel size to the design-system font-size scale (min 14px). */
  private snapToTypeScale(px: number): number {
    const scale = [14, 16, 18, 20, 24, 30, 32, 36];
    return scale.reduce((prev, curr) =>
      Math.abs(curr - px) < Math.abs(prev - px) ? curr : prev
    );
  }

  /** The API has no fund-category field yet, so the company type stands in for it. */
  private getCategoryLabel(type: string): string {
    if (!type) return '';
    const key = `Opportunities.CATEGORY_${type}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : type.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (this.defaultOpportunityImageUrl && !img.src.endsWith(this.defaultOpportunityImageUrl)) {
      img.src = this.defaultOpportunityImageUrl;
    }
  }

  private mapStatusToEnum(status: string): EFundStatus {
    switch (status) {
      case 'Active': return EFundStatus.Current;
      case 'Upcoming': return EFundStatus.Upcoming;
      case 'Ended':
      case 'Completed': return EFundStatus.Ended;
      default: return status as unknown as EFundStatus;
    }
  }

  private getLabelForStatus(status: string, isEn: boolean): string {
    switch (status) {
      case 'Active':
      case EFundStatus.Current as unknown as string:
        return isEn ? 'Active' : 'نشط';
      case 'Upcoming':
      case EFundStatus.Upcoming as unknown as string:
        return isEn ? 'Upcoming' : 'قادم';
      case 'Ended':
      case 'Completed':
      case EFundStatus.Ended as unknown as string:
        return isEn ? 'Ended' : 'منتهي';
      default:
        return status || '';
    }
  }
}